import _, { kebabCase } from 'lodash';
import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import styled, { css } from 'styled-components';
import {
  CustomerPortalButtonShapeRadius,
  CustomerPortalTheme,
  CustomerPortalTypographyType,
} from 'api/customer-portal/theme-api';
import { useGetCpThemeById } from 'hooks/customer-portal/use-cp-theme-hooks';

const getFont = (
  theme: CustomerPortalTheme,
  type: CustomerPortalTypographyType = CustomerPortalTypographyType.ParaSmall,
) => {
  const paraSmall = theme?.themeDefinition?.typography?.find?.(
    (e) => e.typographyType === type,
  );

  if (paraSmall?.fontName) {
    return `${paraSmall.fontName}, 'SF Pro Display', "Segoe UI", Arial, sans-serif`;
  }

  return "'SF Pro Display', 'Segoe UI', Arial, sans-serif";
};
const Span = styled.span<{ cpTheme: CustomerPortalTheme }>`
  height: 100%;
  width: auto;
  margin-bottom: 0;
  --font-family: ${(props) => getFont(props.cpTheme)};
  font-family: var(--font-family);

  ${(props) =>
    props?.cpTheme?.themeDefinition?.typography?.map(
      (e) => css`
    --cp-${kebabCase(e?.typographyType)}-font-weight: ${e?.weight};
    --cp-${kebabCase(e?.typographyType)}-font-family: ${
        e?.fontName ?? "'SF Pro Display', 'Segoe UI', Arial, sans-serif"
      };
    --cp-${kebabCase(e?.typographyType)}-font-size: ${e?.size};
    --cp-${kebabCase(e?.typographyType)}-line-height: 120%;
  `,
    )};

  ${(props) =>
    props?.cpTheme?.themeDefinition?.buttons?.map(
      (e) => css`
      --cp-${kebabCase(e?.buttonType)}-button-font-family: ${
        e?.fontName ?? "'SF Pro Display', 'Segoe UI', Arial, sans-serif"
      };
      --cp-${kebabCase(e?.buttonType)}-button-bg: ${e?.backgroundColor?.color};
      --cp-${kebabCase(e?.buttonType)}-button-border-width: ${e?.outline};
      --cp-${kebabCase(e?.buttonType)}-button-border-radius: ${
        CustomerPortalButtonShapeRadius[e?.shape]
      };
      --cp-${kebabCase(e?.buttonType)}-button-font-size: ${e?.size};
      --cp-${kebabCase(e?.buttonType)}-button-color: ${e?.textColor?.color};
      --cp-${kebabCase(e?.buttonType)}-button-font-weight: ${e?.weight};
      --cp-${kebabCase(e?.buttonType)}-button-border-color: ${
        e?.borderColor?.color
      };
  `,
    )};

  .cp-heading-01-primary,
  .cp-heading-01-secondary,
  .cp-heading-01-link,
  .cp-heading-01-disabled,
  .cp-heading-01-placeholder {
    font-family: var(--cp-heading-1-font-family);
    --font-family: var(--cp-heading-1-font-family);
  }

  .cp-heading-02-primary,
  .cp-heading-02-secondary,
  .cp-heading-02-link,
  .cp-heading-02-disabled,
  .cp-heading-02-placeholder {
    font-family: var(--cp-heading-2-font-family);
    --font-family: var(--cp-heading-2-font-family);
  }

  .cp-heading-03-primary,
  .cp-heading-03-secondary,
  .cp-heading-03-link,
  .cp-heading-03-disabled,
  .cp-heading-03-placeholder {
    font-family: var(--cp-heading-3-font-family);
    --font-family: var(--cp-heading-3-font-family);
  }

  .cp-heading-04-primary,
  .cp-heading-04-secondary,
  .cp-heading-04-link,
  .cp-heading-04-disabled,
  .cp-heading-04-placeholder {
    font-family: var(--cp-heading-4-font-family);
    --font-family: var(--cp-heading-4-font-family);
  }

  @media (max-width: 768px) {
    --cp-heading-1-font-size: 3.052rem;
    --cp-heading-1-font-weight: 400;
    --cp-heading-2-font-size: 2.441rem;
    --cp-heading-2-font-weight: 400;
    --cp-heading-3-font-size: 1.953rem;
    --cp-heading-3-font-weight: 400;
    --cp-heading-4-font-size: 1.625rem;
    --cp-heading-4-font-weight: 400;
    --cp-heading-1-line-height: normal;
    --cp-heading-2-line-height: normal;
    --cp-heading-3-line-height: normal;
    --cp-heading-4-line-height: normal;
  }

  @media (max-width: 480px) {
    --cp-heading-1-font-size: 1.953rem;
    --cp-heading-1-font-weight: 400;
    --cp-heading-2-font-size: 1.625rem;
    --cp-heading-2-font-weight: 400;
    --cp-heading-3-font-size: 1.5rem;
    --cp-heading-3-font-weight: 400;
    --cp-heading-4-font-size: 1.375rem;
    --cp-heading-4-font-weight: 400;
  }
`;

export const LoadCssForTheme: React.FC<{ theme: CustomerPortalTheme }> = ({
  theme,
}) => {
  const fonts = useMemo(() => {
    const typoFamilies =
      theme?.themeDefinition?.typography?.map?.((e) => e?.fontName) ?? [];
    const buttonFamilies =
      theme?.themeDefinition?.buttons?.map?.((e) => e?.fontName) ?? [];

    return _.uniq(typoFamilies.concat(buttonFamilies));
  }, [theme]);

  return (
    <Helmet>
      {fonts.map((f) => (
        <link
          href={`https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,400;0,700;1,400;1,700&family=${f}:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;1,100;1,200;1,300;1,400;1,500;1,600;1,700&display=swap`}
          rel="stylesheet"
        />
      ))}
    </Helmet>
  );
};

export const ThemeVariablesWrapper: React.FC<{ themeId: number }> = ({
  children,
  themeId,
}) => {
  const { data: theme } = useGetCpThemeById(themeId);

  if (!_.isFinite(themeId) || !theme) {
    return <>{children}</>;
  }

  return (
    <Span cpTheme={theme}>
      {children}
      {theme ? <LoadCssForTheme theme={theme} /> : <></>}
    </Span>
  );
};

export const CpModalRoot = styled.div.attrs({ id: 'cp-modal-root' })`
  .ant-modal-wrap {
    overflow: hidden;
  }
`;
